import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import { utils } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import ElementSettingsContext from '../helpers/element-settings-context';
import getBatchValues from '../helpers/get-batch-values';

const { SliderPattern } = designSystem;

const getPopoverProps = (prop) => {
  if (!prop) return {};

  return {
    ...prop,
    content: prop.renderContent?.() ?? prop.content ?? '',
  };
};

const isIndeterminate = (attributes) =>
  attributes.some((value, index, values) => value !== values[0]);

function SliderControl({
  id,
  elementIds,
  attributes,
  title,
  iconGlyph,
  mixedLabel,
  step,
  min,
  max,
  items = [],
  popover = null,
  valuePopover = null,
  computeFallbackValue = null,
  onChange,
}) {
  const { state } = useContext(ElementSettingsContext);

  const values = useMemo(
    () => getBatchValues(state, { elementIds, attributes, computeFallbackValue }),
    [state, elementIds, attributes, computeFallbackValue]
  );
  const flattenedValues = values.flat();
  const indeterminate = isIndeterminate(flattenedValues);
  const currentValue =
    Number(indeterminate ? Math.max(...flattenedValues) : flattenedValues[0]) || min;
  const isMultipleElements = elementIds.length > 1;

  const popoverProps = useMemo(() => getPopoverProps(popover), [popover]);
  const valuePopoverProps = useMemo(() => getPopoverProps(valuePopover), [valuePopover]);

  const handleChange = useCallback(
    ({ targetId, targetAttributes, value, skipTracking }) => {
      onChange({
        controlId: targetId,
        elementIds,
        attributes: targetAttributes,
        value,
        skipTracking,
      });
    },
    [elementIds, onChange]
  );

  const throttledHandleChange = useMemo(
    () =>
      throttle((targetId, targetAttributes, value) => {
        handleChange({
          targetId,
          targetAttributes,
          value: `${value}`,
          skipTracking: true,
        });
      }, 50),
    [handleChange]
  );

  const createSliderChangeHandler = useCallback(
    (targetId, targetAttributes) => (value) => {
      if (!utils.isNumeric(Number(value))) {
        return;
      }

      throttledHandleChange(targetId, targetAttributes, value);
    },
    [throttledHandleChange]
  );

  const createSliderAfterChangeHandler = useCallback(
    (targetId, targetAttributes) => (value) => {
      if (!utils.isNumeric(Number(value))) {
        return;
      }

      handleChange({
        targetId,
        targetAttributes,
        value: `${value}`,
      });
    },
    [handleChange]
  );

  const sliderItems = useMemo(
    () =>
      items.length > 0
        ? items.map((item, index) => {
            const subAttributesValues = [values[index]].flat();
            const subIndeterminate = isMultipleElements && isIndeterminate(subAttributesValues);
            const subCurrentValue =
              Number(
                subIndeterminate ? Math.max(...subAttributesValues) : subAttributesValues[0]
              ) || min;

            return {
              value: subCurrentValue,
              iconGlyph: item.iconGlyph,
              popover: getPopoverProps(item.popover),
              indeterminate: subIndeterminate,
              ...(item.valuePopover && {
                valuePopoverProps: getPopoverProps(item.valuePopover),
              }),
              onChange: createSliderChangeHandler(item.id, item.attributes),
              onAfterChange: createSliderAfterChangeHandler(item.id, item.attributes),
            };
          })
        : null,
    [
      values,
      createSliderAfterChangeHandler,
      createSliderChangeHandler,
      isMultipleElements,
      min,
      items,
    ]
  );

  return (
    <SliderPattern
      value={currentValue}
      title={title}
      items={sliderItems}
      iconGlyph={iconGlyph}
      mixedLabel={mixedLabel}
      step={step}
      min={min}
      max={max}
      indeterminate={indeterminate}
      popoverProps={popoverProps}
      {...(valuePopover && { valuePopoverProps })}
      onChange={createSliderChangeHandler(id, attributes)}
      onAfterChange={createSliderAfterChangeHandler(id, attributes)}
    />
  );
}

SliderControl.propTypes = {
  id: PropTypes.string.isRequired,
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  attributes: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  iconGlyph: PropTypes.string.isRequired,
  mixedLabel: PropTypes.string.isRequired,
  step: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      iconGlyph: PropTypes.string,
      popover: PropTypes.object,
      valuePopover: PropTypes.object,
      attributes: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  popover: PropTypes.object,
  valuePopover: PropTypes.object,
  computeFallbackValue: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

export default SliderControl;
